import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { 
  Calendar, Clock, Users, Phone, Mail, ArrowLeft, ChevronLeft, ChevronRight,
  CheckCircle, AlertCircle, User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';

export default function BookTablePage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1); // 1: Date/Time, 2: Details, 3: Confirmation
  
  // Booking form state
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [partySize, setPartySize] = useState(2);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [bookingDetails, setBookingDetails] = useState({
    customer_name: user?.name || '',
    customer_phone: user?.phone || '',
    customer_email: user?.email || '',
    special_requests: ''
  });
  const [bookingResult, setBookingResult] = useState(null);

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    fetchRestaurant();
  }, [id]);

  useEffect(() => {
    if (user) {
      setBookingDetails(prev => ({
        ...prev,
        customer_name: user.name || prev.customer_name,
        customer_email: user.email || prev.customer_email,
        customer_phone: user.phone || prev.customer_phone
      }));
    }
  }, [user]);

  useEffect(() => {
    if (selectedDate && partySize) {
      fetchAvailableSlots();
    }
  }, [selectedDate, partySize]);

  const fetchRestaurant = async () => {
    try {
      const response = await api.get(`/restaurants/${id}`);
      setRestaurant(response.data);
      
      // Check if table booking is enabled
      if (!response.data.table_booking_enabled) {
        toast.error('This restaurant does not accept table bookings');
        navigate(`/restaurant/${id}`);
      }
    } catch (error) {
      console.error('Failed to fetch restaurant:', error);
      toast.error('Restaurant not found');
      navigate('/restaurants');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSlots = async () => {
    setSlotsLoading(true);
    try {
      const response = await api.get(`/restaurants/${id}/available-slots?date=${selectedDate}&party_size=${partySize}`);
      setAvailableSlots(response.data.available_slots || []);
      setSelectedTime(''); // Reset selected time when date changes
    } catch (error) {
      console.error('Failed to fetch slots:', error);
      setAvailableSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleSubmitBooking = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error('Please select a date and time');
      return;
    }
    
    if (!bookingDetails.customer_name || !bookingDetails.customer_phone || !bookingDetails.customer_email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/table-bookings', {
        restaurant_id: id,
        date: selectedDate,
        time: selectedTime,
        party_size: partySize,
        customer_name: bookingDetails.customer_name,
        customer_phone: bookingDetails.customer_phone,
        customer_email: bookingDetails.customer_email,
        special_requests: bookingDetails.special_requests
      });
      
      setBookingResult(response.data);
      setStep(3);
      toast.success('Table booked successfully!');
    } catch (error) {
      console.error('Failed to create booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to book table');
    } finally {
      setSubmitting(false);
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Add empty cells for days before the first day
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const formatDateForAPI = (date) => {
    return date.toISOString().split('T')[0];
  };

  const isDateSelectable = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today;
  };

  const handleDateSelect = (date) => {
    if (!isDateSelectable(date)) return;
    setSelectedDate(formatDateForAPI(date));
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 pt-20">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-200 rounded w-1/3"></div>
            <div className="h-64 bg-slate-200 rounded-2xl"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!restaurant) return null;

  return (
    <div className="min-h-screen bg-slate-50 pt-20 pb-12">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate(`/restaurant/${id}`)}
            className="mb-4"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to {restaurant.name}
          </Button>
          
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" data-testid="page-title">
            Book a Table at {restaurant.name}
          </h1>
          <p className="text-slate-500 mt-1">{restaurant.address}, {restaurant.city}</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                step >= s 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-slate-200 text-slate-500'
              }`}>
                {step > s ? <CheckCircle className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-16 h-1 mx-2 ${step > s ? 'bg-orange-500' : 'bg-slate-200'}`}></div>
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Date & Time Selection */}
        {step === 1 && (
          <Card data-testid="step-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-orange-500" />
                Select Date & Time
              </CardTitle>
              <CardDescription>Choose when you'd like to visit</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Party Size */}
              <div>
                <Label className="text-base font-medium mb-3 block">Party Size</Label>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setPartySize(Math.max(1, partySize - 1))}
                    disabled={partySize <= 1}
                    data-testid="decrease-party"
                  >
                    -
                  </Button>
                  <div className="w-20 text-center">
                    <span className="text-2xl font-bold text-slate-900">{partySize}</span>
                    <p className="text-xs text-slate-500">{partySize === 1 ? 'guest' : 'guests'}</p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setPartySize(Math.min(20, partySize + 1))}
                    disabled={partySize >= 20}
                    data-testid="increase-party"
                  >
                    +
                  </Button>
                </div>
              </div>

              {/* Calendar */}
              <div>
                <Label className="text-base font-medium mb-3 block">Select Date</Label>
                <div className="bg-white border rounded-xl p-4">
                  <div className="flex items-center justify-between mb-4">
                    <Button variant="ghost" size="icon" onClick={prevMonth}>
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <span className="font-semibold">
                      {currentMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                    </span>
                    <Button variant="ghost" size="icon" onClick={nextMonth}>
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-7 gap-1 text-center text-sm">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} className="py-2 text-slate-500 font-medium">{day}</div>
                    ))}
                    {getDaysInMonth(currentMonth).map((date, idx) => (
                      <button
                        key={idx}
                        onClick={() => date && handleDateSelect(date)}
                        disabled={!isDateSelectable(date)}
                        className={`py-2 rounded-lg transition-colors ${
                          !date 
                            ? '' 
                            : !isDateSelectable(date)
                              ? 'text-slate-300 cursor-not-allowed'
                              : selectedDate === formatDateForAPI(date)
                                ? 'bg-orange-500 text-white font-semibold'
                                : 'hover:bg-orange-100 text-slate-700'
                        }`}
                        data-testid={date ? `date-${formatDateForAPI(date)}` : undefined}
                      >
                        {date?.getDate()}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Time Slots */}
              {selectedDate && (
                <div>
                  <Label className="text-base font-medium mb-3 block">Select Time</Label>
                  {slotsLoading ? (
                    <div className="flex gap-2 flex-wrap">
                      {[1,2,3,4].map(i => (
                        <div key={i} className="h-10 w-20 bg-slate-200 rounded-lg animate-pulse"></div>
                      ))}
                    </div>
                  ) : availableSlots.length === 0 ? (
                    <div className="bg-amber-50 text-amber-700 p-4 rounded-lg flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      <span>No available slots on this date. Please try another date.</span>
                    </div>
                  ) : (
                    <div className="flex gap-2 flex-wrap">
                      {availableSlots.map(slot => (
                        <Button
                          key={slot.time}
                          variant={selectedTime === slot.time ? 'default' : 'outline'}
                          className={`${selectedTime === slot.time ? 'btn-primary' : ''} ${!slot.is_available ? 'opacity-50 cursor-not-allowed' : ''}`}
                          onClick={() => slot.is_available && setSelectedTime(slot.time)}
                          disabled={!slot.is_available}
                          data-testid={`time-${slot.time}`}
                        >
                          <Clock className="w-4 h-4 mr-1" />
                          {slot.time}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <Button 
                className="btn-primary w-full"
                onClick={() => setStep(2)}
                disabled={!selectedDate || !selectedTime}
                data-testid="continue-step-1"
              >
                Continue to Details
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Contact Details */}
        {step === 2 && (
          <Card data-testid="step-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-orange-500" />
                Your Details
              </CardTitle>
              <CardDescription>
                Booking for {partySize} {partySize === 1 ? 'guest' : 'guests'} on {selectedDate} at {selectedTime}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  placeholder="Enter your name"
                  value={bookingDetails.customer_name}
                  onChange={(e) => setBookingDetails(prev => ({ ...prev, customer_name: e.target.value }))}
                  data-testid="input-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="Enter your phone number"
                  value={bookingDetails.customer_phone}
                  onChange={(e) => setBookingDetails(prev => ({ ...prev, customer_phone: e.target.value }))}
                  data-testid="input-phone"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={bookingDetails.customer_email}
                  onChange={(e) => setBookingDetails(prev => ({ ...prev, customer_email: e.target.value }))}
                  data-testid="input-email"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="requests">Special Requests (optional)</Label>
                <Textarea
                  id="requests"
                  placeholder="e.g., High chair needed, food allergies, special occasion..."
                  value={bookingDetails.special_requests}
                  onChange={(e) => setBookingDetails(prev => ({ ...prev, special_requests: e.target.value }))}
                  rows={3}
                  data-testid="input-requests"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                  Back
                </Button>
                <Button 
                  className="btn-primary flex-1"
                  onClick={handleSubmitBooking}
                  disabled={submitting || !bookingDetails.customer_name || !bookingDetails.customer_phone || !bookingDetails.customer_email}
                  data-testid="submit-booking"
                >
                  {submitting ? 'Booking...' : 'Confirm Booking'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && (
          <Card data-testid="step-3">
            <CardContent className="text-center py-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Booking Confirmed!</h2>
              <p className="text-slate-500 mb-6">
                Your table has been reserved at {restaurant.name}
              </p>
              
              <div className="bg-slate-50 rounded-xl p-6 text-left max-w-sm mx-auto mb-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-orange-500" />
                    <span className="font-medium">{selectedDate}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-orange-500" />
                    <span className="font-medium">{selectedTime}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-orange-500" />
                    <span className="font-medium">{partySize} {partySize === 1 ? 'guest' : 'guests'}</span>
                  </div>
                </div>
              </div>
              
              <p className="text-sm text-slate-500 mb-6">
                A confirmation email has been sent to {bookingDetails.customer_email}
              </p>
              
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={() => navigate('/orders')}>
                  View My Bookings
                </Button>
                <Button className="btn-primary" onClick={() => navigate(`/restaurant/${id}`)}>
                  Back to Restaurant
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
